<template>
  <section v-if="totalJobCount > 0 || editable" class="company__section company__section--work">
    <header>
      <section>
        <h2 class="section-heading">{{ totalJobCount === 1
          ? I18n.t('job_openings_at_company', { name: company.name })
          : I18n.t('num_job_openings_at_company', { name: company.name, num: totalJobCount })
        }}</h2>
        <span v-if="editable">
          <a :href="`/companies/${company.slug}/jobs/new`" class="pill-button">
            <span class="pill-button__text">{{ I18n.t('create_job_post') }}</span>
            <span class="pill-button__icon"><i class="ss-icon">add</i></span>
          </a>
          <a
            v-if="atsProvider"
            class="pill-button"
            :href="`/companies/${company.slug}/disconnect_ats`"
            rel="nofollow"
            data-method="post"
            :data-confirm="I18n.t('confirm_gallery_disconnect')"
          >
            <span class="pill-button__text">{{ `Disconnect ${atsProvider}` }}</span>
            <span class="pill-button__icon"><i class="ss-icon">delete</i></span>
          </a>
        </span>
      </section>
    </header>
    <div class="jobs">
      <div class="company__job" v-for="(job, n) in jobs">
        <a :href="`/companies/${company.slug}/jobs/${job.id}/edit`" class="company__job__edit" v-if="editable">
          <i class="ss-icon">compose</i>
        </a>
        <a :href="`/companies/${company.slug}/jobs/${job.id}/click`" class="company__job__link">
          <div class="company__job__info">
            <span class="company__job__title">{{ job.title }}</span>
            <time class="company__job__timestamp" :datetime="job.created_at">{{ formatTimeAgo(job.created_at) }} ago</time>
          </div>
          <div class="company__job__details">
            <div class="company__job__location-details">
              <div class="company__job__location" v-if="job.location && !job.remote">{{ job.location }}</div>
              <div class="company__job__location company__job__location--remote" v-if="job.remote">Remote</div>
              <div class="company__job__location company__job__location--remote" v-else-if="job.remote_friendly">Remote Friendly</div>
              <div class="company__job__location__icon">{{ job.location && !job.remote ? "location" : "wifi" }}</div>
            </div>
            <span class="company__job__hours">{{ formatCommitment(job.commitment.name) }}</span>
          </div>
        </a>
      </div>
    </div>
    <footer v-if="jobs.length < totalJobCount">
      <template v-if="editable">
        <button
          class="cm-button"
          type="button"
          @click="fetchNextPage()"
        >More</button>
        <button
          class="cm-button"
          type="button"
          @click="fetchAll()"
        >{{ I18n.t('view_all_num_jobs', { num: totalJobCount }) }}</button>
      </template>

      <template v-else>
        <a class="cm-button" :href="`/jobs?company_id=${company.id}`">
          {{ I18n.t('view_all_num_jobs', { num: totalJobCount }) }}
        </a>
      </template>
    </footer>
  </section>
</template>

<script>

  import elasticSearch from "../lib/elastic-search";
  import { getURLParams } from "../lib/url";
  import { formatDistanceStrict, parseISO } from "date-fns";

  export default {
    props: ['company', 'editable'],

    data () {
      return {
        response: null,

        page: 0,
        size: 5,
      }
    },

    created () {
      this.fetchResults()
        .then(response => {
          this.response = response;
        }).catch(error => {
          console.error(error);
        }).then(() => {
          this.isLoading = false;
        });
    },

    computed: {
      query () {
        return {
          size: this.size,
          sort: [
            { created_at: "desc" },
            "_score"
          ],
          query: {
            bool: {
              must: [
                { match: { visible: true } },
                { match: { "company.application_status": "published" } },
                { match: { "company.id": this.company.id } }
              ]
            }
          }
        };
      },

      jobs () {
        if (!this.response) return [];
        return this.response.hits.hits.map(hit => hit._source);
      },

      totalJobCount () {
        return this.response ? this.response.hits.total : 0
      },

      atsProvider () {
        return this.company.ats_provider
          ? this.company.ats_provider.toLowerCase()
            .split(' ')
            .map((s) => s.charAt(0).toUpperCase() + s.substring(1))
            .join(' ')
          : null
      }
    },

    methods: {
      fetchResults(page=0) {
        return elasticSearch({
          index: "jobs",
          size: this.size,
          from: this.size * page,
          body: this.query,
        });
      },

      fetchNextPage() {
        this.fetchResults(this.page + 1)
          .then(response => {
            this.response.hits.hits.push(...response.hits.hits);

            this.page++;
          }).catch(error => {
            console.error(error);
          }).then(() => {
            this.isLoading = false;
          });
      },

      fetchAll() {
        elasticSearch({
          index: "jobs",
          size: 9999,
          body: this.query,
        })
          .then(response => {
            this.response = response;
          }).catch(error => {
            console.error(error);
          }).then(() => {
            this.isLoading = false;
          });
      },

      formatCommitment (str) {
        return str.split('_').map((s) => { return s.charAt(0).toUpperCase() + s.slice(1) }).join('-')
      },

      formatTimeAgo(time) {
        return formatDistanceStrict(parseISO(time), new Date());
      }
    }
  }
</script>

<style lang="scss" scoped>
  .jobs {
    border-radius: $radius;
  }
  .company__job {
    border-bottom: 0;
    display: flex;
    text-decoration: none;
    outline: none;

    & + & {
      border-top: 1px solid var(--bg-2);
    }

    &__edit {
      display: block;
      padding: 1.1rem 0 1rem 1rem;
      text-decoration: none;
      color: var(--fg-2);
    }

    &__link {
      box-sizing: border-box;
      display: flex;
      text-decoration: none;
      flex: 1;
      padding: 1rem;
    }

    &__info, &__details {
      // flex: 1;
    }

    &__info {
      display: flex;
      flex-direction: column;
      flex: 1 0 50%;
    }

    &__details {
      display: flex;
      flex: 0 1 50%;
      justify-content: flex-end;
      align-items: center;
      text-align: right;
      color: var(--fg-2);
    }

    @include max-width($bp_menu_collapsed) {
      // padding: 15px;
    }

    &__title {
      @extend %b1e;
      padding-right: 5%;
      // width: 45%;

      a {
        text-decoration: none;
      }

      i {
        font-size: $fs_small;
      }
    }

    &__location, &__hours {

    }

    &__timestamp {
      @extend %b3;
      color: var(--fg-2);
    }

    &__location-details {
      position: relative;
      padding-right: 1rem;
    }

    &__location {
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;

      &--remote {
      }

      &__icon {
        @include icon;
        position: absolute;
        right: 0;
        top: 50%;
        transform: translateY(-0.5rem);

        content: 'location';
        font-size: $fs_large_body;
        margin-left: 0.25rem;
        vertical-align: middle;
        overflow: visible;
      }
    }

    &__hours {
      flex-shrink: 0;
      margin-left: 1rem;
      width: 7rem;
      white-space: nowrap;

      &:after {
        @include icon;

        content: 'clock';
        font-size: $fs_large_body;
        margin-left: 0.25rem;
        vertical-align: middle;
      }

      @include max-width($bp_menu_collapsed) {
        display: none;
      }
    }
  }

  footer {
    display: flex;
    justify-content: center;
    margin-top: $gutter;

    .cm-button + .cm-button {
      margin-left: 0.5rem;
    }
  }
</style>



// WEBPACK FOOTER //
// app/javascript/company/jobs.vue?594c1f00